import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Mail, Sparkles } from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'
import { AuthCard } from '@/components/auth/AuthCard'
import { AuthForm, Field } from '@/components/auth/AuthForm'
import { useAuth } from '@/lib/auth'
import { isEmailNotConfirmedError, isNonexistentUserError } from '@/lib/auth-errors'

/**
 * Resolve the post-sign-in destination, preferring (in order):
 *   1. `?next=...` query param — used by invite landing pages and any
 *      future emailed-link flow that needs a round-trip back.
 *   2. router `location.state.from` — set by ProtectedRoute on redirect.
 *   3. `/account` as a sensible default.
 * Only accepts same-origin internal paths (starting with `/`, not `//`)
 * to avoid open-redirect surface area.
 */
function safeNextPath(rawNext: string | null, fromState: string | undefined): string {
  const candidate = rawNext ?? fromState ?? '/account'
  if (candidate.startsWith('/') && !candidate.startsWith('//')) return candidate
  return '/account'
}

export function SignInPage() {
  const { signIn, session, loading, resendVerification, signInWithMagicLink } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const nextPath = safeNextPath(
    searchParams.get('next'),
    (location.state as { from?: string } | null)?.from,
  )

  // Incoming routing context:
  //  - state.authError : AuthErrorBoundary forwarded a Supabase verify
  //    failure via URL fragment (#error=…&error_description=…).
  const incomingState = location.state as { authError?: string; from?: string } | null
  const [email, setEmail] = useState('')

  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(incomingState?.authError ?? null)
  // Pre-arm the Resend control whenever AuthErrorBoundary forwarded
  // an auth-link error (typically otp_expired). The banner copy tells
  // the user to "Sign in below, or use Resend / Forgot Password" but
  // the Resend button was previously only rendered after a failed
  // sign-in attempt that returned email_not_confirmed — so users
  // saw a banner advertising an action that wasn't available.
  // (PR #12 review.)
  const [needsConfirmation, setNeedsConfirmation] = useState<boolean>(
    Boolean(incomingState?.authError),
  )
  const [resendBusy, setResendBusy] = useState(false)
  const [resendSent, setResendSent] = useState(false)

  // Magic-link mode: the page defaults to email/password; the user can switch
  // to a passwordless "email me a sign-in link" flow (handy when they've
  // forgotten their password).
  const [mode, setMode] = useState<'password' | 'magic'>('password')
  const [magicBusy, setMagicBusy] = useState(false)
  const [magicSent, setMagicSent] = useState(false)

  // /sign-in?confirmed=1 — destination for Supabase Auth's email-verify
  // link. Supabase auto-signs the user in as part of confirming, so by
  // the time we land here a session usually exists. We override the
  // session-already-exists redirect to show the banner first, then let
  // the user click through.
  const confirmed = searchParams.get('confirmed') === '1'
  // /sign-in?logged-out=1 — destination after Sign Out (from SiteHeader
  // and AccountLayout). Surfaces a "You've been signed out" banner so
  // the action has visible confirmation.
  const loggedOut = searchParams.get('logged-out') === '1'


  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setNeedsConfirmation(false)
    setResendSent(false)
    const { error: signInError } = await signIn(email, password)
    setSubmitting(false)
    if (!signInError) {
      navigate(nextPath, { replace: true })
      return
    }
    if (isEmailNotConfirmedError(signInError)) {
      setNeedsConfirmation(true)
      setError(`This email isn't confirmed yet. Check ${email} for the verification link, or resend it below.`)
      return
    }
    setError(signInError.message)
  }

  async function onResend() {
    if (!email.trim()) {
      setError('Enter your email above first, then click Resend.')
      return
    }
    setResendBusy(true)
    setResendSent(false)
    const { error: resendError } = await resendVerification(email.trim())
    setResendBusy(false)
    if (resendError) {
      setError(resendError.message)
      return
    }
    setResendSent(true)
  }

  async function onMagicSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!email.trim()) {
      setError('Enter your email first.')
      return
    }
    setMagicBusy(true)
    setError(null)
    // Carry the same post-auth destination the password form uses, so a
    // magic-link sign-in from an invite (/sign-in?next=<invite URL>) still
    // returns to the invite page instead of dropping the user on /account.
    const { error: magicError } = await signInWithMagicLink(email.trim(), nextPath)
    setMagicBusy(false)
    // Non-enumerating: a genuine "no such account" (shouldCreateUser:false)
    // gets the same generic confirmation as a real send. Only surface
    // unexpected errors (rate limit, network).
    if (magicError && !isNonexistentUserError(magicError)) {
      setError(magicError.message)
      return
    }
    setMagicSent(true)
  }

  function switchMode(nextMode: 'password' | 'magic') {
    setMode(nextMode)
    setError(null)
    setMagicSent(false)
    setNeedsConfirmation(false)
    setResendSent(false)
  }

  // When ?confirmed=1 is present we explicitly want the user to see the
  // success banner before going anywhere, so we don't auto-redirect on
  // session-present here.
  if (!loading && session && !confirmed) {
    return <Navigate to={nextPath} replace />
  }

  return (
    <SiteLayout>
      <AuthCard
        title="Sign In"
        subtitle={
          confirmed
            ? 'Your email is verified. Sign in below to continue.'
            : loggedOut
              ? 'You’ve been signed out. Sign in below to log back in.'
              : mode === 'magic'
                ? 'Enter your email and we’ll send a one-time sign-in link — no password needed.'
                : 'Sign in to manage your API keys and usage.'
        }
        footer={
          <>
            Don&apos;t have an account?{' '}
            <Link
              to={
                searchParams.get('next')
                  ? `/sign-up?next=${encodeURIComponent(searchParams.get('next') ?? '')}`
                  : '/sign-up'
              }
              className="text-[var(--color-accent)] underline underline-offset-2"
            >
              Create one
            </Link>
            .
          </>
        }
      >
        {confirmed ? (
          <div
            role="status"
            className={[
              'mb-4 flex items-start gap-3',
              'rounded-[var(--radius-DEFAULT)] border border-[var(--color-accent)]/40',
              'bg-[var(--color-accent-soft)] px-3 py-2.5',
              'text-[13px] text-[var(--color-text-strong)]',
            ].join(' ')}
          >
            <CheckCircle2 size={16} className="text-[var(--color-accent)] shrink-0 mt-0.5" aria-hidden />
            <span>
              <strong>Email verified.</strong>{' '}
              {session
                ? 'You’re already signed in.'
                : 'Sign in below to continue.'}
            </span>
          </div>
        ) : loggedOut ? (
          <div
            role="status"
            className={[
              'mb-4 flex items-start gap-3',
              'rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)]',
              'bg-[var(--color-elev)] px-3 py-2.5',
              'text-[13px] text-[var(--color-text-strong)]',
            ].join(' ')}
          >
            <CheckCircle2 size={16} className="text-[var(--color-accent)] shrink-0 mt-0.5" aria-hidden />
            <span>
              <strong>You’ve been signed out.</strong>{' '}
              Sign in below to continue.
            </span>
          </div>
        ) : null}
        {confirmed && session ? (
          <Link
            to={nextPath}
            className={[
              'inline-flex items-center justify-center gap-2',
              'w-full mt-2 px-4 py-2.5 rounded-[var(--radius-DEFAULT)]',
              'bg-[var(--color-accent)] text-[var(--color-surface)]',
              'text-[14px] font-medium cursor-pointer',
              'hover:bg-[var(--color-accent-hover)] transition-colors',
            ].join(' ')}
          >
            Continue to your account
          </Link>
        ) : null}
        {mode === 'magic' ? (
          magicSent ? (
            <div className="flex flex-col items-center gap-4 py-2">
              <div
                aria-hidden
                className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
              >
                <Mail size={26} strokeWidth={2} />
              </div>
              <p className="text-[13px] text-[var(--color-muted)] text-center">
                If an account exists for{' '}
                <span className="font-medium text-[var(--color-text)]">{email.trim()}</span>, a
                sign-in link is on its way. Check your inbox (and spam folder). The link expires in
                1 hour.
              </p>
              <button
                type="button"
                onClick={() => switchMode('password')}
                className="text-[12.5px] text-[var(--color-muted)] hover:text-[var(--color-accent)] cursor-pointer transition-colors"
              >
                Back to password sign-in
              </button>
            </div>
          ) : (
            <>
              <AuthForm
                onSubmit={onMagicSubmit}
                submitLabel="Email me a sign-in link"
                submitting={magicBusy}
                error={error}
              >
                <Field
                  id="magic-email"
                  label="Email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </AuthForm>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => switchMode('password')}
                  className="text-center text-[12.5px] text-[var(--color-muted)] hover:text-[var(--color-accent)] underline-offset-2 hover:underline py-1 cursor-pointer"
                >
                  Sign in with a password instead
                </button>
              </div>
            </>
          )
        ) : (
          <>
            <AuthForm
              onSubmit={onSubmit}
              submitLabel="Sign In"
              submitting={submitting}
              error={error}
            >
              <Field
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                placeholder="you@example.com"
              />
              <Field
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                minLength={8}
              />
            </AuthForm>
            {/* Forgot-password lives on its own page (/forgot-password) per
                user preference — single-purpose form keeps the mental model
                clean. The resend-verification button below is contextual,
                only shown when sign-in failed because the email isn't
                confirmed (or after a successful resend). The magic-link
                button offers a passwordless alternative (also a graceful
                path for anyone who's forgotten their password). */}
            <div className="mt-4 flex flex-col gap-2">
              {needsConfirmation || resendSent ? (
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resendBusy}
                  className={[
                    'inline-flex items-center justify-center gap-2',
                    'px-3 py-2 rounded-[var(--radius-DEFAULT)]',
                    'border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)]',
                    'text-[13px] text-[var(--color-accent)] font-medium',
                    'hover:bg-[var(--color-accent-soft)]/80 transition-colors cursor-pointer',
                    'disabled:opacity-60',
                  ].join(' ')}
                >
                  <Mail size={14} aria-hidden />
                  {resendBusy
                    ? 'Sending…'
                    : resendSent
                      ? 'Verification email sent — check your inbox'
                      : 'Resend verification email'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => switchMode('magic')}
                className={[
                  'inline-flex items-center justify-center gap-2',
                  'px-3 py-2 rounded-[var(--radius-DEFAULT)]',
                  'border border-[var(--color-border)]',
                  'text-[13px] text-[var(--color-text)] font-medium',
                  'hover:border-[var(--color-accent)]/60 hover:text-[var(--color-accent)]',
                  'transition-colors cursor-pointer',
                ].join(' ')}
              >
                <Sparkles size={14} aria-hidden />
                Sign in with a magic link
              </button>
              <Link
                to="/forgot-password"
                className="text-center text-[12.5px] text-[var(--color-muted)] hover:text-[var(--color-accent)] underline-offset-2 hover:underline py-1"
              >
                Forgot Password?
              </Link>
            </div>
          </>
        )}
      </AuthCard>
    </SiteLayout>
  )
}
